import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import projectService from '../services/projectService';

/**
 * Custom hook for project management
 * Provides methods and state for projects using React Query
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.projectId - Optional project ID to focus on a specific project
 * @param {boolean} options.includeDocuments - Whether to include documents in project queries
 * @param {boolean} options.includeFeatures - Whether to include features in project queries
 * @param {string} options.initialStatus - Initial status filter (defaults to 'active')
 * @returns {Object} Project state and methods
 */
const useProject = ({ 
  projectId = null, 
  includeDocuments = false,
  includeFeatures = false,
  initialStatus = 'active'
} = {}) => {
  const queryClient = useQueryClient();
  const [projectFilters, setProjectFilters] = useState({
    status: initialStatus,
    search: '',
    page: 1,
    limit: 60,
    type: 'project' // Only fetch projects with type='project'
  });

  // Get all projects with optional filtering
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
    refetch: refetchProjects
  } = useQuery(
    ['projects', projectFilters],
    () => projectService.getProjects(projectFilters),
    {
      enabled: !projectId,
      keepPreviousData: true
    }
  );

  // Get a specific project by ID
  const {
    data: project,
    isLoading: isLoadingProject,
    error: projectError,
    refetch: refetchProject
  } = useQuery(
    ['project', projectId, { includeDocuments, includeFeatures }],
    () => projectService.getProjectById(
      projectId, 
      { includeDocuments, includeFeatures }
    ),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        // Update projects cache if we have this project in the list
        queryClient.setQueryData(
          ['projects', projectFilters],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: oldData.data.map(p => p._id === data._id ? data : p)
            };
          }
        );
      }
    }
  );
  
  // Get project statistics
  const {
    data: projectStats,
    isLoading: isLoadingStats,
    error: statsError,
    refetch: refetchStats
  } = useQuery(
    ['projectStats', projectId],
    () => projectService.getProjectStats(projectId),
    {
      enabled: !!projectId,
      refetchOnWindowFocus: false
    }
  );

  // Create a new project
  const createProjectMutation = useMutation(
    (projectData) => projectService.createProject(projectData),
    {
      onSuccess: () => {
        // Invalidate projects query to refetch
        queryClient.invalidateQueries(['projects']);
      }
    }
  );

  // Update a project
  const updateProjectMutation = useMutation(
    ({ projectId, projectData }) => projectService.updateProject(projectId, projectData),
    {
      onSuccess: (updatedProject) => {
        const projectIdToUse = updatedProject._id || updatedProject.id;
        
        // Update specific project in cache - invalidate all variations of the query
        queryClient.setQueriesData(
          ['project', projectIdToUse],
          (oldData) => {
            // Preserve any existing query-specific data while updating the project
            return oldData ? { ...oldData, ...updatedProject } : updatedProject;
          }
        );
        
        // Also invalidate queries to ensure fresh data
        queryClient.invalidateQueries(['project', projectIdToUse]);
        
        // Update project in projects list
        queryClient.setQueryData(
          ['projects', projectFilters],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: oldData.data.map(p => 
                (p._id === projectIdToUse || p.id === projectIdToUse) ? updatedProject : p
              )
            };
          }
        );
      }
    }
  );

  // Delete a project
  const deleteProjectMutation = useMutation(
    (projectId) => projectService.deleteProject(projectId),
    {
      onSuccess: (_, deletedProjectId) => {
        // Remove from cache
        queryClient.removeQueries(['project', deletedProjectId]);
        
        // Update projects list
        queryClient.setQueryData(
          ['projects', projectFilters],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: oldData.data.filter(p => p._id !== deletedProjectId)
            };
          }
        );
      }
    }
  );
  
  // Archive a project
  const archiveProjectMutation = useMutation(
    (projectId) => projectService.archiveProject(projectId),
    {
      onSuccess: (updatedProject) => {
        // Update specific project in cache
        queryClient.setQueryData(['project', updatedProject._id], updatedProject);
        
        // Update project in projects list or remove if filtered by active
        queryClient.setQueryData(
          ['projects', projectFilters],
          (oldData) => {
            if (!oldData) return oldData;
            
            // If filtering by active status, remove archived project
            if (projectFilters.status === 'active') {
              return {
                ...oldData,
                data: oldData.data.filter(p => p._id !== updatedProject._id)
              };
            }
            
            // Otherwise update it
            return {
              ...oldData,
              data: oldData.data.map(p => 
                p._id === updatedProject._id ? updatedProject : p
              )
            };
          }
        );
      }
    }
  );
  
  // Clone a project
  const cloneProjectMutation = useMutation(
    ({ projectId, options = {} }) => {
      return projectService.cloneProject(projectId, options);
    },
    {
      onSuccess: (newProject) => {
        // Invalidate projects query to refetch list
        queryClient.invalidateQueries(['projects']);
      }
    }
  );
  
  // Toggle task completion status
  const toggleTaskStatusMutation = useMutation(
    ({ projectId, taskId }) => {
      return projectService.toggleTaskStatus(projectId, taskId);
    },
    {
      onSuccess: (result) => {
        const { project } = result;
        // Update project in cache
        queryClient.setQueryData(['project', project._id], project);
        
        // Update project in projects list
        queryClient.setQueryData(
          ['projects', projectFilters],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: oldData.data.map(p => 
                p._id === project._id ? project : p
              )
            };
          }
        );
      }
    }
  );
  
  // Update last visited project
  const updateLastVisitedMutation = useMutation(
    (projectId) => projectService.updateLastVisited(projectId)
  );

  // Function to change project filters
  const setFilters = useCallback((newFilters) => {
    setProjectFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset page to 1 if search or status changes
      page: (newFilters.search !== undefined && newFilters.search !== prev.search) ||
            (newFilters.status !== undefined && newFilters.status !== prev.status) 
            ? 1 : prev.page
    }));
  }, []);

  // Track user visiting a project
  const trackProjectVisit = useCallback((projectId) => {
    if (projectId) {
      updateLastVisitedMutation.mutate(projectId);
    }
  }, [updateLastVisitedMutation]);
  
  // Clear project cache
  const clearProjectCache = useCallback((projectId) => {
    if (projectId) {
      queryClient.removeQueries(['project', projectId]);
    } else {
      queryClient.invalidateQueries('projects');
    }
  }, [queryClient]);

  // Add explicit getProject function to allow manual refetching
  const getProject = useCallback((id) => {
    // Use the id parameter if provided, otherwise use the hook's projectId
    const targetId = id || projectId;
    if (targetId) {
      return queryClient.fetchQuery(
        ['project', targetId, { includeDocuments, includeFeatures }],
        () => projectService.getProjectById(
          targetId, 
          { includeDocuments, includeFeatures }
        )
      );
    }
    return Promise.resolve(null);
  }, [projectId, includeDocuments, includeFeatures, queryClient]);

  return {
    // Projects list data
    projects: projectsData?.data || [],
    projectsPagination: projectsData?.pagination,
    isLoadingProjects,
    projectsError,
    projectFilters,
    setFilters,
    
    // Single project data
    project,
    isLoadingProject,
    projectError,
    
    // Project statistics
    projectStats,
    isLoadingStats,
    statsError,
    
    // Operations
    refetchProjects,
    refetchProject,
    refetchStats,
    getProject, // Add the new getProject function
    
    // Mutations
    createProject: createProjectMutation.mutateAsync,
    isCreatingProject: createProjectMutation.isLoading,
    
    updateProject: updateProjectMutation.mutateAsync,
    isUpdatingProject: updateProjectMutation.isLoading,
    
    deleteProject: deleteProjectMutation.mutateAsync,
    isDeletingProject: deleteProjectMutation.isLoading,
    
    archiveProject: archiveProjectMutation.mutateAsync,
    isArchivingProject: archiveProjectMutation.isLoading,
    
    cloneProject: cloneProjectMutation.mutateAsync,
    isCloningProject: cloneProjectMutation.isLoading,
    
    toggleTaskStatus: toggleTaskStatusMutation.mutateAsync,
    isTogglingTaskStatus: toggleTaskStatusMutation.isLoading,
    
    trackProjectVisit,
    clearProjectCache
  };
};

export default useProject;